import { useEffect } from "react";
import { useRecordContext, useTranslate } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { useIsMobile } from "@/hooks/use-mobile";

import { buildLeadSourceChoices } from "../deals/leadSourceRules";
import { BUSINESS_UNITS, type Contact } from "../types";
import { ContactLinkInput } from "./ContactLinkInput";
import { useContactMandatory, useLeadSources } from "./useContactMandatory";

const MAX_PROJECT_CHOICES = 200;

/**
 * Almikia Epic EC2a: Origen (mandatory, fed by `lead_sources`, the current value always stays
 * selectable) with the link its source asks for (Referido: the referring contact; Colega: the
 * colega contact), then the mandatory business unit (BPO asks the project). Dependent values
 * that no longer apply are cleared on change.
 */
export const ContactOriginInputs = () => {
  const translate = useTranslate();
  const record = useRecordContext<Contact>();
  const { setValue, getValues } = useFormContext();
  const { validate, originLinkKind, sourcesLoaded } = useContactMandatory();
  const { data: sources } = useLeadSources();
  const businessUnit = useWatch({ name: "business_unit" });
  const isMobile = useIsMobile();

  const choices = buildLeadSourceChoices(sources ?? [], record?.lead_source_id);

  // Stale dependent values are cleared as soon as the origin stops asking for them. Waits for
  // the catalogue so an edit form never wipes a saved link while the sources still load.
  useEffect(() => {
    if (!sourcesLoaded) return;
    if (originLinkKind !== "contact" && getValues("referred_by_contact_id")) {
      setValue("referred_by_contact_id", null, { shouldDirty: true });
    }
    if (originLinkKind !== "colega" && getValues("origin_colega_contact_id")) {
      setValue("origin_colega_contact_id", null, { shouldDirty: true });
    }
  }, [originLinkKind, sourcesLoaded, getValues, setValue]);

  useEffect(() => {
    if (businessUnit !== "bpo" && getValues("business_project_id")) {
      setValue("business_project_id", null, { shouldDirty: true });
    }
  }, [businessUnit, getValues, setValue]);

  return (
    <div className="flex flex-col gap-4">
      <SelectInput
        source="lead_source_id"
        showError
        label="resources.contacts.fields.lead_source_id"
        choices={choices}
        optionText="name"
        optionValue="id"
        parse={(value: string | number | null) =>
          value == null || value === "" ? null : Number(value)
        }
        helperText={false}
        validate={validate("lead_source_id")}
      />
      {originLinkKind === "contact" && (
        <ContactLinkInput
          source="referred_by_contact_id"
          label="resources.contacts.fields.referred_by_contact_id"
          validate={validate("referred_by_contact_id")}
        />
      )}
      {originLinkKind === "colega" && (
        <ContactLinkInput
          source="origin_colega_contact_id"
          label="resources.contacts.fields.origin_colega_contact_id"
          filter={{ "contact_types@cs": "{colega}" }}
          validate={validate("origin_colega_contact_id")}
        />
      )}
      <SelectInput
        source="business_unit"
        showError
        label="resources.contacts.fields.business_unit"
        choices={BUSINESS_UNITS.map((unit) => ({
          id: unit,
          name: translate(`resources.contacts.business_units.${unit}`),
        }))}
        translateChoice={false}
        helperText={false}
        validate={validate("business_unit")}
      />
      {businessUnit === "bpo" && (
        <ReferenceInput
          source="business_project_id"
          reference="projects"
          perPage={MAX_PROJECT_CHOICES}
          filter={{ "archived@eq": false }}
          sort={{ field: "name", order: "ASC" }}
        >
          <AutocompleteInput
            label="resources.contacts.fields.business_project_id"
            optionText="name"
            helperText={false}
            validate={validate("business_project_id")}
            modal={isMobile}
          />
        </ReferenceInput>
      )}
    </div>
  );
};
